import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getStatusCode, measureOnFailure, measureOnSuccess } from './utils';

export const AJAX_REQUEST = 'ajax_request';
export const isBrowser = () => typeof window !== 'undefined' && typeof window.document !== 'undefined';

export type CustomAxiosConfig = AxiosRequestConfig & {
    serviceName: string;
    routeKey: string;
    startedAt: number;
    identifier: string;
};

export const handleRequestOnFulfilled = (config: Partial<CustomAxiosConfig> = {}) => {
    const isFromBrowser = isBrowser();
    const { serviceName, routeKey, timeout, ...axiosConfig } = (config || {}) as CustomAxiosConfig;

    const name: string = isFromBrowser ? AJAX_REQUEST : serviceName;

    return {
        ...axiosConfig,
        startedAt: Date.now(),
        timeout,
        identifier: `${name}.${routeKey}`,
    };
};
export const handleRequestOnRejected = (error) => Promise.reject(error);

export const handleResponseOnFulfilled = (response: AxiosResponse) => {
    const { identifier, startedAt } = (response?.config as CustomAxiosConfig) || {};

    measureOnSuccess({
        routeId: identifier,
        startTime: startedAt,
        status: response?.status,
        requestType: 'http',
    });

    return response;
};
export const handleResponseOnRejected = (error: AxiosError) => {
    const { identifier, startedAt } = (error.config as CustomAxiosConfig) || {};
    const status = getStatusCode(error);

    measureOnFailure({
        routeId: identifier,
        startTime: startedAt,
        status,
        requestType: 'http',
    });

    throw error;
};

/**
 * Intercept all axios requests and responses for monitoring successes and failures
 */
export const monitoringInterceptor = (axiosInstance: AxiosInstance) => {
    if (!axiosInstance) {
        return;
    }

    axiosInstance.interceptors.request.use(handleRequestOnFulfilled, handleRequestOnRejected);
    axiosInstance.interceptors.response.use(handleResponseOnFulfilled, handleResponseOnRejected);
};

/**
 * maxios is an axios instance which supports extended axios config. It allows us to make axios requests
 * and get metrics that can help us investigate the behavior of each request without sending
 * the metrics manually.
 *
 * routeKey - path unique id (pathfinder value) - for AJAX routes
 * axiosConfig - external configuration object - for external requests on the server
 */
const axiosInstanceSingleton = axios.create();
monitoringInterceptor(axiosInstanceSingleton);
export const maxios: AxiosInstance = axiosInstanceSingleton; // fixme remove any
